import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Alert, Card, CardContent, CheckboxInput, } from '@guildeducationinc/recess';
import { camelCase, omit, sortBy, uniq } from 'lodash';
import { useContext, useEffect, useMemo, useState } from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useEmployees } from '@src/hooks/useEmployees';
import { EntityType } from '@src/types';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useMutation } from '@apollo/client';
import EmailIcon from '@guildeducationinc/recess/icons/EmailIcon';
import FileCopyIcon from '@guildeducationinc/recess/icons/FileCopyIcon';
import SettingsOutlinedIcon from '@guildeducationinc/recess/icons/SettingsOutlinedIcon';
import { AdminTopNav } from '@src/components/Admin/AdminTopNav/AdminTopNav';
import { FieldSelectionModal } from '@src/components/Admin/FieldSelectionModal/FieldSelectionModal';
import { ViewEmployeeSnapshotButton } from '@src/components/Admin/ViewEmployeeSnapshotButton/ViewEmployeeSnapshotButton';
import { AdminContext } from '@src/contexts/AdminContextProvider';
import { CHECK_CORPORATE_ELIGIBILITY, EMPLOYEES, SETUP_USER } from '@src/graphql/queries/employee';
import { useAuth } from '@src/hooks/useAuth';
import { useClientIdParam } from '@src/hooks/useClientIdParam';
import { useClients } from '@src/hooks/useClients';
import { ActivityIcon } from 'lucide-react';
import { DataTable, DropdownSelect, FieldType, Form, MessageType, Role, SearchFilterBar, TrackableButton, useAlert, useFields, useLocale, usePreferenceState, useSessionState, } from 'pe-core-frontend';
import { useNavigate } from 'react-router-dom';
export const DEFAULT_FIELDS = [
    'first_name',
    'middle_name',
    'last_name',
    'email',
    'unique_id',
    'hire_date',
    'employment_type',
    'employment_status',
    'hours_per_week',
    'phone',
];
export const Employees = ({ showClientPicker = false }) => {
    const initialClientId = useClientIdParam();
    const navigate = useNavigate();
    const { t, formatToLocalDatetime } = useLocale();
    const { isSuperuser, isClaimsProcessor, isMemberSupport } = useAuth();
    const { showAlert } = useAlert();
    const { isUsingGuildAuth } = useContext(AdminContext);
    const [searchText, setSearchText] = useSessionState('employeeSearch', '');
    const [clientId, setClientId] = useSessionState('employeeClientId', initialClientId);
    useEffect(() => {
        if (initialClientId) {
            setClientId(initialClientId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialClientId]);
    const [userSetup, setUserSetup] = useState(null);
    const [userSetupError, setUserSetupError] = useState('');
    const [eligibilityCheckData, setEligibilityCheckData] = useState(null);
    const [eligibilityCheckError, setEligibilityCheckError] = useState([]);
    const [eligibilityCheckSuccess, setEligibilityCheckSuccess] = useState('');
    const [isEligible, setIsEligible] = useState(null);
    const roleOptions = [
        {
            value: Role.EMPLOYEE,
            label: t(Role.EMPLOYEE),
        },
        {
            value: Role.MANAGER,
            label: t(Role.MANAGER),
        },
        {
            value: Role.CLIENT_ADMIN,
            label: t(Role.CLIENT_ADMIN),
        },
    ];
    // custom fields not in metadata
    const customFields = [
        {
            id: -1,
            clientId: clientId ?? -1,
            entityType: EntityType.EMPLOYEE,
            name: t('Product Access Sync Status'),
            code: 'productAccessSyncStatus',
            type: FieldType.STRING,
            isRequired: false,
            isActive: true,
            isSystem: true,
            isLookup: false,
        },
        {
            id: -2,
            clientId: clientId ?? -1,
            entityType: EntityType.EMPLOYEE,
            name: t('Product Access Synced At'),
            code: 'productAccessSyncedAt',
            type: FieldType.DATE_TIME,
            isRequired: false,
            isActive: true,
            isSystem: true,
            isLookup: false,
        },
    ];
    const UserSetupFields = useMemo(() => {
        const fields = [
            {
                name: 'email',
                label: t('Email'),
                type: 'text',
                disabled: true,
            },
            {
                name: 'firstName',
                label: t('First Name'),
                type: 'text',
                disabled: true,
            },
            {
                name: 'lastName',
                label: t('Last Name'),
                type: 'text',
                disabled: true,
            },
            {
                name: 'password',
                label: t('Password'),
                type: 'password',
                hidden: !isSuperuser || (isUsingGuildAuth && Boolean(userSetup?.userId)),
                infoText: isUsingGuildAuth
                    ? t('This client is using Guild Auth. Password is still required, but the value will not be used for Guild Auth.')
                    : undefined,
            },
            {
                name: 'confirmPassword',
                label: t('Confirm Password'),
                type: 'password',
                hidden: !isSuperuser || (isUsingGuildAuth && Boolean(userSetup?.userId)),
                infoText: isUsingGuildAuth
                    ? t('This client is using Guild Auth. Password is still required, but the value will not be used for Guild Auth.')
                    : undefined,
            },
            {
                name: 'roleNames',
                label: t('Roles'),
                type: 'lookup',
                options: roleOptions,
                multiple: true,
                hidden: !isSuperuser,
            },
        ];
        return fields;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userSetup, roleOptions, isSuperuser, isUsingGuildAuth]);
    const CorporateEligibilityCheckFields = useMemo(() => {
        const fields = [
            {
                name: 'checkDate',
                label: t('Check Date'),
                type: 'date',
            },
        ];
        return fields;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const [showOnlySignedUp, setShowOnlySignedUp] = useSessionState('showOnlySignedUp', false);
    const { employees, loading, error, page, pageSize, setPage, setPageSize, orderBy, setOrderBy, orderByDir, setOrderByDir, total, } = useEmployees({
        clientId,
        searchText,
        onlySignedUp: showOnlySignedUp,
    });
    const { items: metadata } = useFields({
        clientId,
        entityType: EntityType.EMPLOYEE,
    });
    const [selectedFields, setSelectedFields] = usePreferenceState(`employeeColumnsToDisplay_${clientId}`, DEFAULT_FIELDS);
    const gridColumns = useMemo(() => {
        const columns = [];
        if (!metadata)
            return columns;
        for (const fieldInfo of metadata) {
            if (selectedFields.includes(fieldInfo.code)) {
                columns.push({
                    field: camelCase(fieldInfo.code),
                    headerName: fieldInfo.name,
                    flex: 0.2,
                    sortable: DEFAULT_FIELDS.includes(fieldInfo.code),
                    renderCell: ({ row }) => {
                        if (fieldInfo.code === 'role_names') {
                            return row.roleNames.map(t).join(', ');
                        }
                        return undefined;
                    },
                });
            }
        }
        columns.push({
            field: 'signedUp',
            headerName: t('Signed Up?'),
            flex: 0.2,
            sortable: false,
            renderCell: ({ row }) => _jsx("div", { children: row.signedUp ? t('Yes') : t('No') }),
        });
        // custom fields not in metadata
        if (selectedFields.includes('productAccessSyncStatus')) {
            columns.push({
                field: 'productAccessSyncStatus',
                headerName: t('Product Access Sync Status'),
                flex: 0.2,
                sortable: true,
            });
        }
        if (selectedFields.includes('productAccessSyncedAt')) {
            columns.push({
                field: 'productAccessSyncedAt',
                headerName: t('Product Access Synced At'),
                flex: 0.2,
                sortable: true,
                renderCell: ({ row }) => (_jsx("span", { children: row.productAccessSyncedAt
                        ? formatToLocalDatetime(row.productAccessSyncedAt, {
                            dateStyle: 'short',
                            timeStyle: 'medium',
                        })
                        : '' })),
            });
        }
        return columns;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [metadata, selectedFields]);
    const [showCustomize, setShowCustomize] = useState(false);
    const refetchQueries = (result) => {
        if (result.data.setupUser?.success !== true)
            return [];
        return [EMPLOYEES];
    };
    const [setupUser] = useMutation(SETUP_USER, {
        refetchQueries,
    });
    const [checkCorporateEligibility] = useMutation(CHECK_CORPORATE_ELIGIBILITY);
    const { clients } = useClients({ initialPageSize: 100 }); // we need to show all clients in the list
    const clientOptions = useMemo(() => {
        return clients.map(client => ({
            label: client.name,
            value: client.id,
        }));
    }, [clients]);
    const handleClientChange = (e) => {
        setClientId(Number(e.target.value));
    };
    const handleUserSetup = async (userSetup) => {
        try {
            setUserSetupError('');
            const response = await setupUser({
                variables: {
                    input: {
                        ...omit(userSetup, 'confirmPassword', 'userId'),
                    },
                },
            });
            if (response.data.setupUser?.success === true) {
                showAlert(t('User saved'));
                setUserSetup(null);
            }
            else {
                const errors = {};
                response.data.setupUser?.messages.forEach((message) => {
                    if (message.type === MessageType.ERROR) {
                        if (message.sender) {
                            errors[message.sender] = t(message.value);
                        }
                        else {
                            setUserSetupError(t(message.value));
                            return false;
                        }
                    }
                });
                return errors;
            }
        }
        catch (e) {
            setUserSetupError(t(e.message));
        }
    };
    const handleValidate = (userSetup) => {
        const errors = {};
        if (!userSetup.firstName) {
            errors.firstName = t('{field} is required', { values: { field: 'First Name' } });
        }
        if (!userSetup.lastName) {
            errors.lastName = t('{field} is required', { values: { field: 'Last Name' } });
        }
        if (!userSetup.email) {
            errors.email = t('{field} is required', { values: { field: 'Email' } });
        }
        if (!userSetup.userId) {
            if (!userSetup.password) {
                if (isUsingGuildAuth) {
                    errors.password = t('Password must be provided, but is not used when using Guild Auth');
                }
                else {
                    errors.password = t('{field} is required', { values: { field: 'Password' } });
                }
            }
            if (!userSetup.confirmPassword) {
                errors.confirmPassword = t('{field} is required', {
                    values: { field: 'Confirm Password' },
                });
            }
        }
        if (userSetup.roleNames.length === 0) {
            errors.roleNames = t('{field} is required', { values: { field: 'Role' } });
        }
        if (userSetup.password &&
            userSetup.confirmPassword &&
            userSetup.password !== userSetup.confirmPassword) {
            errors.confirmPassword = t('{field} does not match {field2}', {
                values: { field: 'Confirm Password', field2: 'Password' },
            });
        }
        return errors;
    };
    const handleEligibilityCheckValidate = (data) => {
        const errors = {};
        if (!data.checkDate) {
            errors.checkDate = t('{field} is required', { values: { field: 'Check Date' } });
        }
        return errors;
    };
    const handleEligibilityCheck = async (data) => {
        try {
            setEligibilityCheckError([]);
            setEligibilityCheckSuccess('');
            setIsEligible(null);
            const response = await checkCorporateEligibility({
                variables: {
                    input: {
                        employeeId: data.employeeId,
                        checkDate: data.checkDate,
                    },
                },
            });
            if (response.data?.checkCorporateEligibility?.success) {
                const result = response.data.checkCorporateEligibility;
                const eligibilityStatus = result.isEligible ? t('Eligible') : t('Not Eligible');
                const message = t('Corporate eligibility check completed: {status}', {
                    values: { status: eligibilityStatus },
                });
                // Set eligibility status
                setIsEligible(result.isEligible);
                // Display success message with eligibility status
                setEligibilityCheckSuccess(message);
                // Also display error messages if any (reasons for not being eligible)
                if (result.messages && result.messages.length > 0) {
                    const errorMessages = result.messages
                        .filter((msg) => msg.type === MessageType.ERROR)
                        .map((msg) => t(msg.value));
                    if (errorMessages.length > 0) {
                        setEligibilityCheckError(errorMessages);
                    }
                }
                // Keep modal open to display the result
                return {};
            }
            else {
                const errors = {};
                const generalErrors = [];
                response.data?.checkCorporateEligibility?.messages.forEach((message) => {
                    if (message.type === MessageType.ERROR) {
                        if (message.sender) {
                            errors[message.sender] = t(message.value);
                        }
                        else {
                            generalErrors.push(t(message.value));
                        }
                    }
                });
                if (generalErrors.length > 0) {
                    setEligibilityCheckError(generalErrors);
                }
                return errors;
            }
        }
        catch (e) {
            setEligibilityCheckError([t(e.message)]);
        }
    };
    const handleSortModelChange = (sortModel) => {
        if (sortModel && sortModel.length > 0) {
            const sort = sortModel[0];
            setOrderBy(sort.field);
            setOrderByDir(sort.sort);
        }
        else {
            setOrderBy(null);
            setOrderByDir(null);
        }
    };
    return (_jsxs("div", { children: [_jsx(AdminTopNav, { title: t('Employees'), actions: showClientPicker && (_jsx("div", { children: _jsx(DropdownSelect, { btnStyle: {
                            padding: '6px 12px',
                            fontWeight: '400',
                        }, label: t('Client'), options: clientOptions, value: Number(clientId || 0), onChange: handleClientChange, color: 'primary', variant: 'contained', isClearable: false }) })), searchFilterActions: _jsx(SearchFilterBar, { enableSearch: true, searchText: searchText, onSearchChange: text => {
                        setSearchText(text);
                        setPage(1);
                    }, searchPlaceholder: t('Search by Name/Email/ID'), enableFilter: true, filterActions: _jsx(CheckboxInput, { label: t('Signed Up?'), checked: showOnlySignedUp, onChange: e => setShowOnlySignedUp(e.currentTarget.checked) }), isFilterActive: showOnlySignedUp }) }), _jsx("div", { children: _jsx(Card, { children: _jsxs(CardContent, { children: [error && (_jsx(Alert, { display: true, hideClose: true, type: 'danger', children: error.message })), !clientId && (_jsx(Alert, { display: true, hideClose: true, type: 'info', children: t('Select a client to view employees') })), clientId && (_jsxs(_Fragment, { children: [_jsx("div", { className: 'flex flexJustify--end margin--bottom_8', children: _jsx(TrackableButton, { btnType: 'icon', enText: 'Customize', tag: 'customize_columns', appearance: 'minimal', label: t('Customize'), icon: _jsx(SettingsOutlinedIcon, { size: 24 }), onClick: () => setShowCustomize(true) }) }), _jsx(DataTable, { disableColumnMenu: true, disableRowSelectionOnClick: true, loading: loading, rows: employees || [], pageSizeOptions: [10, 25], paginationMode: 'server', paginationModel: { page: page - 1, pageSize }, onPaginationModelChange: pagination => {
                                            setPage(pagination.page + 1);
                                            setPageSize(pagination.pageSize);
                                        }, rowCount: total, sortingMode: 'server', onSortModelChange: handleSortModelChange, sortModel: [{ field: orderBy ?? 'uniqueId', sort: orderByDir }], columns: [
                                            ...gridColumns,
                                            {
                                                field: 'action',
                                                headerName: t('Actions'),
                                                renderCell: ({ row }) => (_jsxs("div", { children: [_jsx(TrackableButton, { btnType: 'icon', enText: 'Activity', tag: `employee_${row.id}`, label: t('Activity'), appearance: 'minimal', icon: _jsx(ActivityIcon, { size: 20 }), onClick: () => navigate(`/admin/clients/${row.clientId}/employees/${row.id}/activity`), className: 'margin--right_8', title: t('Activity') }), _jsx(TrackableButton, { btnType: 'icon', enText: 'Employee Notifications', tag: `employee_${row.id}`, label: t('Employee Notifications'), appearance: 'minimal', icon: _jsx(EmailIcon, { size: 20 }), onClick: () => navigate(`/admin/clients/${row.clientId}/employees/${row.id}/notifications`), className: 'margin--right_8', title: t('Employee Notifications') }), (isClaimsProcessor || isMemberSupport) && (_jsx(TrackableButton, { btnType: 'icon', enText: 'Check Corporate Eligibility', tag: `employee_eligibility_check_${row.id}`, label: t('Check Corporate Eligibility'), appearance: 'minimal', icon: _jsx(FileCopyIcon, { size: 20 }), onClick: () => setEligibilityCheckData({
                                                                employeeId: row.id,
                                                                checkDate: null,
                                                            }), className: 'margin--right_8', title: t('Check Corporate Eligibility') })), isSuperuser && (_jsx(TrackableButton, { btnType: 'icon', enText: 'Setup User', tag: `employee_${row.id}`, label: t('Setup User'), appearance: 'minimal', icon: _jsx(SettingsOutlinedIcon, { size: 20 }), onClick: () => setUserSetup({
                                                                userId: row.userId,
                                                                employeeId: row.id,
                                                                firstName: row.firstName ?? '',
                                                                lastName: row.lastName ?? '',
                                                                email: row.email ?? '',
                                                                password: '',
                                                                confirmPassword: '',
                                                                roleNames: uniq(row.roleNames.concat(['employee'])),
                                                            }), className: 'margin--right_8', title: t('Setup User') })), _jsx(ViewEmployeeSnapshotButton, { employee: row })] })),
                                                width: 200,
                                                sortable: false,
                                            },
                                        ] })] })), userSetup && (_jsx(Form, { title: userSetup.userId ? 'Update roles' : 'Setup New User', modal: true, obj: userSetup, fields: UserSetupFields, onValidate: handleValidate, onSave: handleUserSetup, onCancel: () => {
                                    setUserSetup(null);
                                    setUserSetupError('');
                                }, error: userSetupError })), eligibilityCheckData && (_jsx(Form, { title: t('Check Corporate Eligibility'), modal: true, obj: eligibilityCheckData, fields: CorporateEligibilityCheckFields, onValidate: handleEligibilityCheckValidate, onSave: handleEligibilityCheck, onCancel: () => {
                                    setEligibilityCheckData(null);
                                    setEligibilityCheckError([]);
                                    setEligibilityCheckSuccess('');
                                    setIsEligible(null);
                                }, children: () => (_jsxs(_Fragment, { children: [eligibilityCheckSuccess && (_jsx(Alert, { display: true, hideClose: true, type: isEligible === true ? 'success' : 'danger', className: 'margin--bottom_16', children: eligibilityCheckSuccess })), eligibilityCheckError.length > 0 && (_jsx(Alert, { display: true, hideClose: true, type: 'danger', className: 'margin--bottom_16', children: eligibilityCheckError.length === 1 ? (eligibilityCheckError[0]) : (_jsx("ul", { style: { margin: 0, paddingLeft: '20px' }, children: eligibilityCheckError.map((error, index) => (_jsx("li", { children: error }, index))) })) }))] })) })), showCustomize && (_jsx(FieldSelectionModal, { fields: sortBy([...(metadata || []), ...customFields], 'name'), selectedFields: selectedFields, onChange: setSelectedFields, onClose: () => setShowCustomize(false) }))] }) }) })] }));
};
